import axios from "axios";
import { refreshAccessToken } from "../redux/auth/action/LoginAction";
import { store } from "../redux/store";

// Define public endpoints that don't require Authorization headers
const publicEndpoints = [
  "api/events/",
  "api/organizer/",
  "subscription/",
  "login/",
  "register/"
];

// Helper function to check if URL is public (more precise than simple includes)
const isPublicEndpoint = (url) => {
  return publicEndpoints.some(endpoint => {
    // Check if url exactly matches or starts with the endpoint
    return url === endpoint || url.startsWith(`${endpoint}?`) || url.startsWith(`${endpoint}/`);
  });
};

const apiClient = axios.create({
  baseURL: process.env.REACT_APP_API_CLIENT_BASE_URL

  //  Home IP test
  //  baseURL: "http://192.168.1.248:8000/api/"
});

// Request interceptor to attach Authorization header conditionally
apiClient.interceptors.request.use(
  (config) => {
    // Add logging to help debug token issues
    console.log(`[Request] ${config.method.toUpperCase()} ${config.url}`);
    
    const access = localStorage.getItem("access") || sessionStorage.getItem("access");
    const isPublic = isPublicEndpoint(config.url);

    if (access && !isPublic) {
      config.headers.Authorization = `Bearer ${access}`;
      console.log(`[Request] Adding auth header for ${config.url}`);
    } else if (isPublic) {
      console.log(`[Request] Public endpoint, no auth header for ${config.url}`);
    }

    return config;
  },
  (error) => {
    console.error("[Request Error]", error);
    return Promise.reject(error);
  }
);

// Response interceptor to handle 401 errors and refresh token
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const status = error.response ? error.response.status : null;
    
    console.log(`[Response Error] ${status} for ${originalRequest.url}`);

    // Skip token refresh for:
    // 1. Requests already retried (prevent infinite loop)
    // 2. Public endpoints that don't need authentication
    // 3. The login endpoint itself
    // 4. Token refresh endpoint to prevent circular refreshing
    if (
      originalRequest._retry ||
      isPublicEndpoint(originalRequest.url) ||
      originalRequest.url.includes("/token/refresh/")
    ) {
      return Promise.reject(error);
    }

    if (status === 401) {
      console.log(`[401 Error] Attempting to refresh token for ${originalRequest.url}`);
      originalRequest._retry = true;

      try {
        // Dispatch the refresh action and WAIT for the actual token
        const action = await store.dispatch(refreshAccessToken());
        
        // The token should be returned directly from the refreshAccessToken action
        console.log("[Token Refresh] Action result:", action);
        
        // Get the fresh token from storage since refreshAccessToken updates it
        const newToken = localStorage.getItem("access") || sessionStorage.getItem("access");
        
        if (!newToken) {
          throw new Error("Failed to get new token after refresh");
        }
        
        console.log(`[Token Refresh] Got new token, retrying ${originalRequest.url}`);
        
        // Update the original request with the new token
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        
        // Retry the original request with the new token
        return apiClient(originalRequest);
      } catch (refreshError) {
        console.error("[Token Refresh Failed]", refreshError);
        // Let the error propagate to components that need to handle auth failure
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

// Modify your apiClient.get function to handle full URLs:
const get = (endpoint, config = {}) => {
  // Check if endpoint is a full URL (starts with http)
  const url = endpoint.startsWith('http') ? endpoint : `${process.env.REACT_APP_API_CLIENT_BASE_URL}${endpoint}`;
  return axios.get(url, config);
};

export default apiClient;
